import { useMemo } from "react";
import { Calendar, X } from "@phosphor-icons/react";

/**
 * Inline date-range filter with quick presets + custom range pickers.
 *
 * Props:
 *   value     – { from: "YYYY-MM-DD" | "", to: "YYYY-MM-DD" | "" }
 *   onChange  – fn(next) called with the new {from,to} on every change.
 *   testIdPrefix – e.g. "sales-date" yields data-testids:
 *                   `${prefix}-preset`, `${prefix}-from`, `${prefix}-to`,
 *                   `${prefix}-clear`.
 *
 * Presets ("today", "yesterday", "week", "month", "30d") snap both inputs
 * to the corresponding window. "Custom" leaves the inputs as-is so the
 * operator can pick any dates. "All time" clears the filter.
 */
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

function preset(key) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (key === "today") return { from: ymd(today), to: ymd(today) };
  if (key === "yesterday") {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (key === "week") {
    // Monday-anchored week
    const dow = (today.getDay() + 6) % 7; // 0 = Monday
    const start = new Date(today); start.setDate(today.getDate() - dow);
    return { from: ymd(start), to: ymd(today) };
  }
  if (key === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: ymd(start), to: ymd(today) };
  }
  if (key === "30d") {
    const start = new Date(today); start.setDate(today.getDate() - 29);
    return { from: ymd(start), to: ymd(today) };
  }
  if (key === "all") return { from: "", to: "" };
  return null;
}

export default function DateRangeFilter({ value, onChange, testIdPrefix = "date" }) {
  const { from = "", to = "" } = value || {};

  // Detect which preset (if any) the current value matches so we can keep
  // the dropdown in sync when callers set the range programmatically.
  const activePreset = useMemo(() => {
    if (!from && !to) return "all";
    for (const k of ["today", "yesterday", "week", "month", "30d"]) {
      const p = preset(k);
      if (p.from === from && p.to === to) return k;
    }
    return "custom";
  }, [from, to]);

  const onPreset = (e) => {
    const k = e.target.value;
    if (k === "custom") {
      // Keep current range; just flag it as custom (handled implicitly by the
      // memo above on next render — no state change needed).
      onChange({ from, to });
      return;
    }
    onChange(preset(k));
  };

  return (
    <div className="inline-flex items-center gap-2 border border-canvas-line bg-canvas">
      <span className="pl-3 pr-1 py-2 text-ink-muted">
        <Calendar size={12} weight="bold" />
      </span>
      <select
        data-testid={`${testIdPrefix}-preset`}
        value={activePreset}
        onChange={onPreset}
        className="pl-0 pr-2 py-2 bg-canvas text-sm focus:outline-none border-0"
      >
        <option value="all">All time</option>
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="week">This week</option>
        <option value="month">This month</option>
        <option value="30d">Last 30 days</option>
        <option value="custom">Custom</option>
      </select>
      <input
        type="date"
        data-testid={`${testIdPrefix}-from`}
        value={from}
        max={to || undefined}
        onChange={(e) => onChange({ from: e.target.value, to })}
        className="px-2 py-2 bg-canvas text-sm focus:outline-none border-0 border-l border-canvas-line"
        title="From"
      />
      <span className="text-ink-muted text-xs">→</span>
      <input
        type="date"
        data-testid={`${testIdPrefix}-to`}
        value={to}
        min={from || undefined}
        onChange={(e) => onChange({ from, to: e.target.value })}
        className="px-2 py-2 bg-canvas text-sm focus:outline-none border-0"
        title="To"
      />
      {(from || to) && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-clear`}
          onClick={() => onChange({ from: "", to: "" })}
          className="px-2 py-2 text-ink-muted hover:text-ink"
          title="Clear date filter"
        >
          <X size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}
